/**
 * The building blocks every Approval Desk queue is made of.
 *
 * Expenses, timesheets, leave, invoices, purchase orders, estimates, PSV
 * reports, training and receipting each used to style their own toolbar, row
 * and chips, so no two tabs matched. They compose these instead: one toolbar
 * row, one segmented toggle, one row shape, one chip height, one pair of
 * approve/reject buttons, one rule for a status filter beside a period toggle.
 * Status colours stay with StatusBadge, buttons with Button, search with
 * SearchInput.
 */
import type { ComponentType, ReactNode, SVGProps } from 'react';
import Button from './Button';
import RowSelectZone from './RowSelectZone';
import SearchInput from './SearchInput';
import StatusBadge from './StatusBadge';
import { ageChipClass, ageLabel } from '../home/age';
import { TEXT } from './Typography';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * One row: toggles and filters, then the search filling what is left, then
 * the count. It wraps on a narrow screen rather than spending a row on search.
 */
export function QueueToolbar({
  count,
  search,
  children,
}: {
  /** Right-aligned summary, e.g. "12 expenses". */
  count?: ReactNode;
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    ariaLabel?: string;
  };
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 sm:px-6">
      {children}
      {search && (
        <SearchInput
          className="min-w-[12rem] flex-1"
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          ariaLabel={search.ariaLabel}
        />
      )}
      {count != null && (
        <span className={`shrink-0 text-sm text-gray-600 ${search ? '' : 'ml-auto'}`}>{count}</span>
      )}
    </div>
  );
}

/** The desk's tab look (Headless UI `Tab`), shared by every page laid out like it. */
export const DESK_TAB =
  'group relative flex items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 data-[selected]:border-blue-600 data-[selected]:font-semibold data-[selected]:text-gray-900';
export const DESK_TAB_ICON =
  'h-5 w-5 text-gray-500 transition-colors duration-150 group-data-[selected]:text-blue-600';

/**
 * The hairline between two desk tabs: fades out at both ends so it reads as a
 * seam, not a box. `group` separates one set of tabs from the next, in the
 * logo blue, as Home's column divider does.
 */
export function TabDivider({ group = false }: { group?: boolean }) {
  return (
    <span
      aria-hidden
      className={`my-2.5 w-px shrink-0 self-stretch bg-gradient-to-b from-transparent to-transparent ${
        group ? 'mx-2 via-brand/50' : 'via-gray-300'
      }`}
    />
  );
}

/** A desk tab's count. `hot` when the count needs action. Nothing at zero. */
export function TabBadge({ count, hot }: { count: number; hot?: boolean }) {
  if (count === 0) return null;
  return (
    <span
      className={`ml-1.5 rounded px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums transition-colors ${
        hot
          ? 'bg-red-50 text-red-700'
          : 'bg-gray-100 text-gray-500 group-data-[selected]:bg-blue-50 group-data-[selected]:text-blue-600'
      }`}
    >
      {count}
    </span>
  );
}

/** The segmented look, for a switcher SegmentedControl cannot express. */
export const SEGMENT_TRACK = 'inline-flex shrink-0 rounded-md bg-gray-100 p-0.5';
export const SEGMENT_ITEM =
  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 aria-pressed:bg-white aria-pressed:text-gray-900 aria-pressed:shadow-sm';

/**
 * The periods (Current / Past, Pending / All) each status can be listed in,
 * for a queue whose status filter sits beside a period toggle. A status left
 * out can be listed in every period.
 */
export type StatusPeriods<P extends string> = Readonly<Partial<Record<string, readonly P[]>>>;

/**
 * The period to show once a status is chosen: the one on screen if it can
 * list that status, else the first that can (Approved opens Past), so a
 * status is never filtered against a list that cannot hold it.
 */
export function periodForStatus<P extends string>(periods: StatusPeriods<P>, status: string, period: P): P {
  const listedIn = periods[status];
  return !listedIn || listedIn.includes(period) ? period : listedIn[0];
}

/**
 * The status to keep once the period, or what it lists, changes: every status
 * ('') if the period cannot list the one chosen, rather than a list that
 * cannot hold anything.
 */
export function statusForPeriod<P extends string>(periods: StatusPeriods<P>, status: string, period: P): string {
  const listedIn = periods[status];
  return !listedIn || listedIn.includes(period) ? status : '';
}

/**
 * The same rule for a filter whose choices come from the rows loaded (week,
 * job, employee, approver): once a new period, scope or view loads rows that
 * no longer hold the value chosen, it goes back to every value (''). Its
 * dropdown would show "All" while the list stayed filtered by it. Returns
 * `filters` itself when every value is still offered.
 */
export function filtersInOptions<F extends { [K in keyof F]: string }>(
  filters: F,
  options: Partial<Record<keyof F, readonly string[]>>
): F {
  let kept = filters;
  for (const key of Object.keys(options) as Array<keyof F>) {
    const value = filters[key];
    if (value && !options[key]?.includes(value)) kept = { ...kept, [key]: '' };
  }
  return kept;
}

/** A choice between a few views (Mine / All, Current / Past). */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** Accessible name for the group. */
  label: string;
  options: ReadonlyArray<{ value: T; label: ReactNode; title?: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className={SEGMENT_TRACK}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            title={o.title}
            onClick={() => !on && onChange(o.value)}
            className={SEGMENT_ITEM}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const CHIP_TONE = {
  neutral: 'border-gray-200 bg-gray-50 text-gray-700',
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  green: 'border-green-200 bg-green-50 text-green-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  violet: 'border-violet-200 bg-violet-50 text-violet-700',
} as const;
export type ChipTone = keyof typeof CHIP_TONE;

/** The height every chip on a queue row shares. */
const CHIP_SHAPE =
  'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium leading-none';

export function QueueChip({
  tone = 'neutral',
  title,
  onClick,
  children,
}: {
  tone?: ChipTone;
  title?: string;
  /** Makes the chip a button (e.g. a link to the timesheet). It never also opens the row. */
  onClick?: () => void;
  children: ReactNode;
}) {
  if (onClick) {
    return (
      <button
        type="button"
        title={title}
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        className={`${CHIP_SHAPE} ${CHIP_TONE[tone]} transition-colors duration-150 hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600`}
      >
        {children}
      </button>
    );
  }
  return (
    <span title={title} className={`${CHIP_SHAPE} ${CHIP_TONE[tone]}`}>
      {children}
    </span>
  );
}

/** How long an item has waited, on the app's one age ramp (home/age.ts). */
export function AgeChip({ days }: { days: number }) {
  return (
    <span className={`${CHIP_SHAPE} border-transparent ${ageChipClass(days)}`}>
      {ageLabel(days)}
    </span>
  );
}

/** A status, coloured by StatusBadge, at chip height. */
export function StatusChip({ status, label }: { status: string; label?: string }) {
  return (
    <StatusBadge
      status={status}
      label={label}
      className="h-6 shrink-0 whitespace-nowrap leading-none"
    />
  );
}

const TILE_TONE = {
  blue: 'bg-blue-50 text-blue-600',
  violet: 'bg-violet-50 text-violet-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-600',
  gray: 'bg-gray-100 text-gray-600',
} as const;
export type TileTone = keyof typeof TILE_TONE;

export function QueueList({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <ul aria-label={label} className="divide-y divide-gray-200">
      {children}
    </ul>
  );
}

/**
 * One item: an icon tile, the title with its chips on the right, then the
 * lines passed as children, then the actions. A click anywhere on the row
 * opens the item; for keyboard and screen readers the title is the button.
 * The row itself is not a button, because a button's contents are not
 * separately operable and it holds a checkbox, chips and Approve/Reject.
 */
export function QueueRow({
  icon: RowIcon,
  tone = 'blue',
  title,
  chips,
  onOpen,
  select,
  actions,
  expanded,
  isExpanded,
  children,
}: {
  icon: Icon;
  tone?: TileTone;
  title: ReactNode;
  chips?: ReactNode;
  onOpen?: () => void;
  select?: { checked: boolean; onToggle: () => void; label: string };
  actions?: ReactNode;
  /** Detail shown under the row when it is opened in place (lines, days). */
  expanded?: ReactNode;
  /** For a row that opens in place: whether its detail is showing. */
  isExpanded?: boolean;
  children?: ReactNode;
}) {
  return (
    <li>
      <div
        onClick={onOpen}
        className={`flex items-start gap-3 px-4 py-3 transition-colors duration-150 sm:px-5 ${
          onOpen ? 'cursor-pointer hover:bg-gray-50' : ''
        } ${select?.checked ? 'bg-blue-50' : ''}`}
      >
        {select && (
          <RowSelectZone
            checked={select.checked}
            onToggle={select.onToggle}
            ariaLabel={select.label}
            className="-my-3 -ml-4 py-3 pl-4 pr-3 sm:-ml-5 sm:pl-5"
          />
        )}
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${TILE_TONE[tone]}`}
          aria-hidden
        >
          <RowIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start justify-between gap-3">
            {onOpen ? (
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={e => {
                  e.stopPropagation();
                  onOpen();
                }}
                className={`min-w-0 truncate rounded-md text-left hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${TEXT.bodyEmphasis}`}
              >
                {title}
              </button>
            ) : (
              <span className={`min-w-0 truncate ${TEXT.bodyEmphasis}`}>{title}</span>
            )}
            {chips && (
              <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                {chips}
              </span>
            )}
          </div>
          {children}
          {actions && (
            // Buttons act on the item; they must not also open it.
            <div
              className="flex flex-wrap items-center gap-2 pt-2"
              onClick={e => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
          {expanded && (
            <div className="pt-2" onClick={e => e.stopPropagation()}>
              {expanded}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

/** Line 2: the figure that matters (amount, hours), then its context. */
export function QueueFigure({ figure, children }: { figure?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${TEXT.bodyMuted}`}>
      {figure != null && <span className={TEXT.emphasis}>{figure}</span>}
      {children}
    </div>
  );
}

/** Free text from the submitter: two lines at most. */
export function QueueNote({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <p title={title} className={`line-clamp-2 break-words ${TEXT.bodyMuted}`}>
      {children}
    </p>
  );
}

/** Who and when. */
export function QueueByline({ children }: { children: ReactNode }) {
  return <p className={`truncate ${TEXT.caption}`}>{children}</p>;
}

/** Inside a byline: the item was submitted or is approved on someone's behalf. */
export function OnBehalfOf({ children }: { children: ReactNode }) {
  return <span className="text-violet-700">{children}</span>;
}

/**
 * "Select all" above a list whose rows can be selected. `label` says so when
 * it leaves some rows out.
 */
export function QueueSelectAll({
  checked,
  onChange,
  label = 'Select all',
}: {
  checked: boolean;
  onChange: () => void;
  label?: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer select-none items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 touch-manipulation sm:px-5">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className={TEXT.bodyMuted}>{label}</span>
    </label>
  );
}

/** What can be done to the selected rows at once. */
export function QueueBulkBar({
  count,
  onClear,
  children,
}: {
  /**
   * The selected rows on screen that can be acted on, and its actions take
   * only those: a selection outlives a filter, period or search change, but a
   * row it hides is never approved unseen.
   */
  count: number;
  onClear: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 sm:px-6">
      <span className={`mr-1 ${TEXT.bodyStrong}`}>{count} selected</span>
      {children}
      <Button size="xs" variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

/** The row's decision buttons. */
export function ApproveRejectButtons({
  onApprove,
  onReject,
  disabled,
  approveLabel = 'Approve',
  rejectLabel = 'Reject',
}: {
  onApprove: () => void;
  onReject: () => void;
  disabled?: boolean;
  approveLabel?: ReactNode;
  rejectLabel?: ReactNode;
}) {
  return (
    <>
      <Button size="xs" variant="success" onClick={onApprove} disabled={disabled}>
        {approveLabel}
      </Button>
      <Button
        size="xs"
        variant="outline"
        onClick={onReject}
        disabled={disabled}
        className="text-red-700 hover:bg-red-50"
      >
        {rejectLabel}
      </Button>
    </>
  );
}
